// Invoices received from suppliers, and the suppliers themselves.
//
// Two tabs rather than two nav entries: suppliers only exist to be invoiced by,
// so they belong next to the invoices rather than competing for the header.

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { PurchaseForm } from '../components/PurchaseForm';
import { SupplierForm } from '../components/SupplierForm';
import {
  ActiveBadge, ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Modal, Pagination, SearchInput,
} from '../components/ui';
import { formatDate, formatMoney } from '@shared/format';
import {
  SUPPLIER_INVOICE_STATUSES, type Supplier, type SupplierInvoice, type SupplierInvoiceStatus,
} from '@shared/types';

const PER_PAGE = 20;

export function Purchases() {
  const t = useT();
  const [tab, setTab] = useState<'invoices' | 'suppliers'>('invoices');

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">
            {tab === 'invoices' ? t('purchases.title') : t('suppliers.title')}
          </h1>
          <p className="page-head__subtitle">{t('purchases.subtitle')}</p>
        </div>
      </div>

      <div className="tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'invoices'}
          className={`tab${tab === 'invoices' ? ' tab--active' : ''}`}
          onClick={() => setTab('invoices')}>
          <Icon name="inbox" size={15} />{t('purchases.title')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'suppliers'}
          className={`tab${tab === 'suppliers' ? ' tab--active' : ''}`}
          onClick={() => setTab('suppliers')}>
          <Icon name="users" size={15} />{t('suppliers.title')}
        </button>
      </div>

      {tab === 'invoices' ? <PurchaseInvoices /> : <Suppliers />}
    </>
  );
}

function PurchaseInvoices() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SupplierInvoiceStatus | ''>('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<SupplierInvoice | 'new'>();
  const [pendingDelete, setPendingDelete] = useState<SupplierInvoice>();
  const [busy, setBusy] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.purchases({ q, status: status || undefined, page, perPage: PER_PAGE }),
    [q, status, page]);

  const currency = (value: number) => formatMoney(value, money);
  const totals = data?.totals;

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.deletePurchase(pendingDelete.id);
      toast.success(t('purchases.deleted'));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {totals && totals.count > 0 && (
        <div className="stats">
          <div className="stat">
            <div className="stat__label">{t('purchases.totals.invoices')}</div>
            <div className="stat__value">{totals.count}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('purchases.totals.net')}</div>
            <div className="stat__value stat__value--muted">{currency(totals.subtotal)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('purchases.totals.tax')}</div>
            <div className="stat__value stat__value--muted">{currency(totals.taxTotal)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('purchases.totals.gross')}</div>
            <div className="stat__value">{currency(totals.total)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('purchases.totals.outstanding')}</div>
            <div className={`stat__value${totals.outstanding > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
              {currency(totals.outstanding)}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('purchases.searchPlaceholder')} />
            <select className="select" style={{ width: 'auto' }} value={status}
              onChange={(e) => { setStatus(e.target.value as SupplierInvoiceStatus | ''); setPage(1); }}
              aria-label={t('common.status')}>
              <option value="">{t('common.all')}</option>
              {SUPPLIER_INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(s === 'paid' ? 'status.paidPurchase' : 'status.pendingPayment')}
                </option>
              ))}
            </select>
            {loading && data && <span className="spinner" />}
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            <Icon name="plus" />{t('purchases.new')}
          </button>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState
              icon={q || status ? 'search' : 'inbox'}
              title={q || status ? t('common.noResults') : t('purchases.empty')}
              hint={q || status ? t('common.noResultsHint') : t('purchases.emptyHint')}
              action={!q && !status && (
                <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
                  <Icon name="plus" />{t('purchases.new')}
                </button>
              )}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="shrink">{t('purchases.field.number')}</th>
                    <th className="shrink">{t('purchases.field.issuedOn')}</th>
                    <th>{t('purchases.field.supplier')}</th>
                    <th className="shrink">{t('purchases.file')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="num">{t('purchases.field.total')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="shrink mono">{invoice.number}</td>
                      <td className="shrink nowrap">{formatDate(invoice.issuedOn, locale)}</td>
                      <td>
                        <button type="button" className="table__primary linklike"
                          onClick={() => setEditing(invoice)}>
                          {invoice.supplierName}
                        </button>
                        <div className="table__meta truncate">
                          {invoice.description || invoice.supplierTaxId}
                        </div>
                      </td>
                      <td className="shrink">
                        {invoice.file ? (
                          <a className="badge badge--accent" href={api.purchaseFileUrl(invoice.id)}
                            title={invoice.file.name}>
                            <Icon name="file-text" size={12} />PDF
                          </a>
                        ) : (
                          <span className="badge badge--pending">{t('purchases.fileMissing')}</span>
                        )}
                      </td>
                      <td className="shrink">
                        <span className={`badge ${invoice.status === 'paid' ? 'badge--paid' : 'badge--pending'}`}>
                          {t(invoice.status === 'paid' ? 'status.paidPurchase' : 'status.pendingPayment')}
                        </span>
                      </td>
                      <td className="num">
                        <div className="table__primary">{currency(invoice.total)}</div>
                        <div className="table__meta">
                          {currency(invoice.subtotal)} + {currency(invoice.taxTotal)}
                        </div>
                      </td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => setEditing(invoice)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            onClick={() => setPendingDelete(invoice)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? t('purchases.new') : t('purchases.edit')}
          wide
          onClose={() => { setEditing(undefined); reload(); }}
        >
          <PurchaseForm
            purchase={editing === 'new' ? undefined : editing}
            onCancel={() => { setEditing(undefined); reload(); }}
            onSaved={(created) => {
              toast.success(t(created ? 'purchases.created' : 'purchases.updated'));
              reload();
            }}
          />
        </Modal>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={t('purchases.deleteConfirm', {
            number: pendingDelete.number, supplier: pendingDelete.supplierName,
          })}
          confirmLabel={t('common.delete')}
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}

function Suppliers() {
  const t = useT();
  const { money } = useSession();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Supplier | 'new'>();
  const [pendingDelete, setPendingDelete] = useState<Supplier>();
  const [busy, setBusy] = useState(false);

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.suppliers({ q, page, perPage: PER_PAGE }), [q, page]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.deleteSupplier(pendingDelete.id);
      toast.success(t('common.deleted'));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder={t('suppliers.searchPlaceholder')} />
            {loading && data && <span className="spinner" />}
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            <Icon name="plus" />{t('suppliers.new')}
          </button>
        </div>

        {error ? <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
          : loading && !data ? <LoadingRow />
          : data && data.items.length === 0 ? (
            <EmptyState icon={q ? 'search' : 'users'}
              title={q ? t('common.noResults') : t('suppliers.empty')}
              hint={q ? t('common.noResultsHint') : t('suppliers.emptyHint')}
              action={!q && (
                <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
                  <Icon name="plus" />{t('suppliers.new')}
                </button>
              )} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('suppliers.field.name')}</th>
                    <th className="shrink">{t('suppliers.field.taxId')}</th>
                    <th>{t('suppliers.field.email')}</th>
                    <th className="shrink num">{t('suppliers.invoices')}</th>
                    <th className="num">{t('suppliers.billed')}</th>
                    <th className="shrink">{t('common.status')}</th>
                    <th className="shrink" />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((supplier) => (
                    <tr key={supplier.id}>
                      <td>
                        <div className="table__primary">{supplier.name}</div>
                        {supplier.address && (
                          <div className="table__meta truncate" style={{ maxWidth: 280 }}>
                            {supplier.address.replace(/\s*\n\s*/g, ', ')}
                          </div>
                        )}
                      </td>
                      <td className="shrink mono">{supplier.taxId || '—'}</td>
                      <td className="table__meta truncate" style={{ maxWidth: 220 }}>
                        {supplier.email || '—'}
                      </td>
                      <td className="shrink num">{supplier.invoiceCount ?? 0}</td>
                      <td className="num table__primary">{formatMoney(supplier.totalBilled ?? 0, money)}</td>
                      <td className="shrink"><ActiveBadge active={supplier.isActive} /></td>
                      <td className="shrink">
                        <div className="table__actions">
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.edit')} aria-label={t('common.edit')}
                            onClick={() => setEditing(supplier)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn--ghost btn--icon"
                            title={t('common.delete')} aria-label={t('common.delete')}
                            onClick={() => setPendingDelete(supplier)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {editing && (
        <Modal title={editing === 'new' ? t('suppliers.new') : t('suppliers.edit')}
          onClose={() => setEditing(undefined)}>
          <SupplierForm
            supplier={editing === 'new' ? undefined : editing}
            onCancel={() => setEditing(undefined)}
            onSaved={(_, created) => {
              toast.success(t(created ? 'suppliers.created' : 'suppliers.updated'));
              setEditing(undefined);
              reload();
            }}
          />
        </Modal>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={(pendingDelete.invoiceCount ?? 0) > 0
            ? t('suppliers.deleteBlocked', {
                name: pendingDelete.name, count: pendingDelete.invoiceCount ?? 0 })
            : t('suppliers.deleteConfirm', { name: pendingDelete.name })}
          confirmLabel={t('common.delete')}
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}
